import { useMemo, useState } from 'react';
import { fmtTRY } from '../../yeni-design/primitives';
import { saParametreleri, useSaHamSorgu, type SaFiltre } from '../useSatisVerisi';
import type { SaKaynak } from '../ekranKaynaklari';
import { sayi, type Sayisal } from '../ozetHesaplari';
import { kisaTutarIsaretsiz, miktarMetni, oranMetni } from '../bicim';
import { tarihEtiketi } from '../ZamanBlok';
import { urunMarji } from '../CokSatanBlok';
import type { DetayGovdeProps } from './detayTipleri';
import { bos, DetayBolum, DetayMesaj, DetayYukleniyor, GunlukCubuklar, OzetKutulari } from './ortak';

/** 990058 `ORDER BY tarih DESC LIMIT 200` (SQL 141). */
export const URUN_FIS_SINIRI = 200;

/** 990057 Satış Analiz · Ürün Günlük (SQL 141). */
export interface UrunGunlukSatiri {
  gun: string;
  lokasyon: string;
  kanal_grup: string;
  kanal: string;
  miktar: Sayisal;
  ciro: Sayisal;
  kar: Sayisal;
}

/** 990058 Satış Analiz · Ürün Fişleri (SQL 141). `tutar` KDV dahil, iade eksi. */
export interface UrunFisSatiri {
  fis_no: Sayisal;
  tarih: string;
  lokasyon: string;
  kanal: string;
  miktar: Sayisal;
  birim_fiyat: Sayisal;
  tutar: Sayisal;
  iade: boolean | string | null;
}

/** Ekranin tek parametre kurali + zorunlu `@STOK` (sozlesme: `Required: true`). Kaynak yoksa sorgu acilmaz. */
export function urunParametreleri(kaynak: SaKaynak | undefined, f: SaFiltre, stokKodu: string): Record<string, string> {
  return kaynak ? { ...saParametreleri(kaynak, f), '@STOK': stokKodu } : {};
}

export function tarihSaatEtiketi(ham: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(ham);
  return m ? `${m[3]}.${m[2]}.${m[1]} ${m[4]}:${m[5]}` : tarihEtiketi(ham);
}

export const iadeMi = (x: unknown): boolean => x === true || x === 'true' || x === 't';

/**
 * Ozet + gunluk seri + sube × kanal. Marj ekranin TEK tanimi (D-FE2, kontrolor kurali): `urunMarji`
 * (CokSatanBlok.tsx) — kar ÷ ciro, ciro 0 ya da kar yoksa null. Ikinci bir marj formulu eklenmez.
 * 990057 `kar` NULL olabilir (son_alis_fiyat yok): toplanan satirlarda (ozet ya da sube × kanal) HIC dolu kar yoksa
 * `urunMarji`ya `kar: null` gider → "—" (CokSatan/KategoriDetay ile ayni); `sayi(null)` 0'i sahte %0 marj yapmaz.
 */
export function urunOzeti(rows: UrunGunlukSatiri[]) {
  let ciro = 0;
  let miktar = 0;
  let kar = 0;
  let karVar = false;
  const gunler = new Map<string, { ciro: number; miktar: number }>();
  const sk = new Map<string, { lokasyon: string; kanal: string; miktar: number; ciro: number; kar: number; karVar: boolean }>();
  for (const r of rows) {
    const c = sayi(r.ciro);
    const m = sayi(r.miktar);
    const k = sayi(r.kar);
    const karDolu = !bos(r.kar);
    ciro += c;
    miktar += m;
    kar += k;
    karVar ||= karDolu;
    const gun = String(r.gun ?? '').slice(0, 10);
    const g = gunler.get(gun) ?? { ciro: 0, miktar: 0 };
    g.ciro += c;
    g.miktar += m;
    gunler.set(gun, g);
    const lokasyon = String(r.lokasyon);
    const kanal = String(r.kanal);
    const anahtar = `${lokasyon}|${kanal}`;
    const s = sk.get(anahtar) ?? { lokasyon, kanal, miktar: 0, ciro: 0, kar: 0, karVar: false };
    s.miktar += m;
    s.ciro += c;
    s.kar += k;
    s.karVar ||= karDolu;
    sk.set(anahtar, s);
  }
  return {
    ciro,
    miktar,
    kar: karVar ? kar : null,
    marj: urunMarji({ ciro, kar: karVar ? kar : null }),
    gunluk: Array.from(gunler.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([gun, g]) => ({ gun, ...g })),
    subeKanal: Array.from(sk.values())
      .sort((a, b) => b.ciro - a.ciro)
      .map((s) => ({ lokasyon: s.lokasyon, kanal: s.kanal, miktar: s.miktar, ciro: s.ciro, marj: urunMarji({ ciro: s.ciro, kar: s.karVar ? s.kar : null }) })),
  };
}

type Olcu = 'ciro' | 'miktar';

/**
 * M6 — urun detayi (spec §7.5, S3). 990057 + 990058, `@STOK` = stok kodu: TAM 2 istek. Kaynaklardan biri yoksa o
 * bolum yazilmaz; ikisi de yoksa (SQL 141 inmemis) bos durum.
 *
 * Sube × kanal tablosu `DagilimTablosu`ya (ortak.tsx) TASINMADI (kontrolor kurali D-FE-M6): o bilesen tek
 * kimlik kolonu + toplama-gore-PAY icin kurulu (`ad`/`ek` tek hucrede, `Pay` = grand-total payi). Burada iki
 * AYRI kimlik kolonu (Şube, Kanal — testler ayri <td> bekler) ve satir-bazli Marj (kar ÷ satirin KENDI cirosu,
 * PAY DEGIL) var; DagilimTablosu'nu bu sekle esnetmek onu iki farkli anlam tasiyan bir bilesene cevirirdi.
 */
export function UrunDetay({ kaynaklar, donem, sube, kanal, stokKodu }: DetayGovdeProps & { stokKodu: string; urunAdi: string }) {
  const [olcu, setOlcu] = useState<Olcu>('ciro');
  const f: SaFiltre = { donem, sube, kanal };
  const gunlukQ = useSaHamSorgu<UrunGunlukSatiri>(
    kaynaklar.urunGunluk, urunParametreleri(kaynaklar.urunGunluk, f, stokKodu),
  );
  const fisQ = useSaHamSorgu<UrunFisSatiri>(
    kaynaklar.urunFisleri, urunParametreleri(kaynaklar.urunFisleri, f, stokKodu),
  );
  const gunlukVeri = gunlukQ.data?.data;
  const oz = useMemo(() => urunOzeti(gunlukVeri ?? []), [gunlukVeri]);

  if (!kaynaklar.urunGunluk && !kaynaklar.urunFisleri)
    return (
      <div data-testid="urun-detay">
        <DetayMesaj testid="urun-detay-kaynak-yok">Ürün detayı bu firmada tanımlı değil.</DetayMesaj>
      </div>
    );

  let satis = null;
  if (kaynaklar.urunGunluk) {
    if (gunlukQ.isLoading) satis = <DetayYukleniyor testid="urun-detay-yukleniyor" />;
    else if (gunlukQ.isError) satis = <DetayMesaj testid="urun-detay-hata">Ürün satışları yüklenemedi.</DetayMesaj>;
    else if ((gunlukVeri ?? []).length === 0) satis = <DetayMesaj testid="urun-detay-bos">Bu ürünün dönemde satışı yok.</DetayMesaj>;
    else
      satis = (
        <>
          <OzetKutulari
            kutular={[
              { testid: 'urun-detay-ozet-ciro', etiket: 'Ciro', deger: fmtTRY(oz.ciro) },
              { testid: 'urun-detay-ozet-miktar', etiket: 'Miktar', deger: miktarMetni(oz.miktar) },
              { testid: 'urun-detay-ozet-marj', etiket: 'Marj', deger: oz.marj === null ? '—' : oranMetni(oz.marj) },
            ]}
          />
          <DetayBolum baslik="Günlük satış">
            <div role="group" aria-label="Çubuk ölçüsü" className="inline-flex self-start rounded-md border border-border overflow-hidden text-[12px]">
              {(['ciro', 'miktar'] as const).map((o) => (
                <button
                  key={o}
                  type="button"
                  data-testid={`urun-detay-olcu-${o}`}
                  aria-pressed={olcu === o}
                  onClick={() => setOlcu(o)}
                  className={`focus-ring px-2.5 py-1 ${olcu === o ? 'bg-ink-900 text-white' : 'bg-white text-ink-700'}`}
                >
                  {o === 'ciro' ? 'Ciro' : 'Miktar'}
                </button>
              ))}
            </div>
            <GunlukCubuklar
              testid="urun-detay-gunluk"
              noktalar={oz.gunluk.map((g) => {
                const deger = olcu === 'ciro' ? g.ciro : g.miktar;
                const metin = olcu === 'ciro' ? fmtTRY(g.ciro) : miktarMetni(g.miktar);
                const kisa = olcu === 'ciro' ? kisaTutarIsaretsiz(g.ciro) : miktarMetni(g.miktar);
                return { anahtar: g.gun, etiket: tarihEtiketi(g.gun), deger, baslik: `${tarihEtiketi(g.gun)}: ${metin}`, kisa };
              })}
            />
          </DetayBolum>
          <DetayBolum baslik="Şube × kanal">
            <div className="overflow-x-auto">
              <table data-testid="urun-detay-sube-kanal" className="w-full text-[13px] border-collapse">
                <thead>
                  <tr className="text-[12px] text-ink-500 border-b border-border">
                    <th className="text-left font-medium py-2 pr-3">Şube</th>
                    <th className="text-left font-medium py-2 px-3">Kanal</th>
                    <th className="text-right font-medium py-2 px-3">Miktar</th>
                    <th className="text-right font-medium py-2 px-3">Ciro</th>
                    <th className="text-right font-medium py-2 pl-3">Marj</th>
                  </tr>
                </thead>
                <tbody>
                  {oz.subeKanal.map((s) => (
                    <tr key={`${s.lokasyon}|${s.kanal}`} data-testid={`urun-detay-sk-${s.lokasyon}-${s.kanal}`} className="border-b border-border last:border-b-0">
                      <td className="py-2 pr-3 text-ink-900">{s.lokasyon}</td>
                      <td className="py-2 px-3 text-ink-700">{s.kanal}</td>
                      <td className="py-2 px-3 text-right tnum text-ink-700">{miktarMetni(s.miktar)}</td>
                      <td className="py-2 px-3 text-right tnum text-ink-900 whitespace-nowrap">{fmtTRY(s.ciro)}</td>
                      <td className={`py-2 pl-3 text-right tnum ${s.marj !== null && s.marj < 0 ? 'text-neg-700' : 'text-ink-500'}`}>
                        {s.marj === null ? '—' : oranMetni(s.marj, 1)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </DetayBolum>
        </>
      );
  }

  let fisler = null;
  if (kaynaklar.urunFisleri) {
    const fisVeri = fisQ.data?.data ?? [];
    let icerik;
    if (fisQ.isLoading) icerik = <DetayYukleniyor testid="urun-detay-fis-yukleniyor" />;
    else if (fisQ.isError) icerik = <DetayMesaj testid="urun-detay-fis-hata">Fişler yüklenemedi.</DetayMesaj>;
    else if (fisVeri.length === 0) icerik = <DetayMesaj testid="urun-detay-fis-bos">Dönemde bu ürünün geçtiği fiş yok.</DetayMesaj>;
    else
      icerik = (
        <>
          <div className="overflow-x-auto">
            <table data-testid="urun-detay-fisler" className="w-full text-[13px] border-collapse">
              <thead>
                <tr className="text-[12px] text-ink-500 border-b border-border">
                  <th className="text-left font-medium py-2 pr-3 whitespace-nowrap">Fiş no</th>
                  <th className="text-left font-medium py-2 px-3">Tarih</th>
                  <th className="text-left font-medium py-2 px-3">Şube</th>
                  <th className="text-left font-medium py-2 px-3">Kanal</th>
                  <th className="text-right font-medium py-2 px-3">Miktar</th>
                  <th className="text-right font-medium py-2 px-3 whitespace-nowrap">Birim fiyat</th>
                  <th className="text-right font-medium py-2 pl-3">Tutar</th>
                </tr>
              </thead>
              <tbody>
                {fisVeri.map((r, i) => {
                  const iade = iadeMi(r.iade);
                  return (
                    // Ayni fiste urun iki satir olabilir: fis_no anahtar olarak TEKIL degil.
                    <tr
                      key={`${String(r.fis_no)}-${i}`}
                      data-testid="urun-detay-fis"
                      data-fis-no={String(r.fis_no)}
                      data-iade={iade ? 'true' : 'false'}
                      className={`border-b border-border last:border-b-0 ${iade ? 'text-neg-700' : 'text-ink-700'}`}
                    >
                      <td className="py-2 pr-3 tnum whitespace-nowrap">
                        {String(r.fis_no)}
                        {iade && (
                          <span data-testid="urun-detay-fis-iade" className="ml-1.5 text-[11px] px-1 rounded bg-neg-50 text-neg-700">
                            İade
                          </span>
                        )}
                      </td>
                      <td className="py-2 px-3 tnum whitespace-nowrap">{tarihSaatEtiketi(String(r.tarih))}</td>
                      <td className="py-2 px-3">{r.lokasyon}</td>
                      <td className="py-2 px-3">{r.kanal}</td>
                      <td className="py-2 px-3 text-right tnum">{miktarMetni(sayi(r.miktar))}</td>
                      <td className="py-2 px-3 text-right tnum whitespace-nowrap">{fmtTRY(sayi(r.birim_fiyat))}</td>
                      <td className="py-2 pl-3 text-right tnum whitespace-nowrap">{fmtTRY(sayi(r.tutar))}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {fisVeri.length >= URUN_FIS_SINIRI && (
            <p data-testid="urun-detay-fis-notu" className="text-[12px] text-ink-500">
              Yalnız son {URUN_FIS_SINIRI} fiş satırı gösteriliyor.
            </p>
          )}
        </>
      );
    fisler = <DetayBolum baslik="Son fişler">{icerik}</DetayBolum>;
  }

  return (
    <div data-testid="urun-detay" className="flex flex-col gap-4">
      {satis}
      {fisler}
    </div>
  );
}
